import { ApiException } from './api'

/**
 * Turn a caught error into a message a page can actually SHOW — never a blank one.
 *
 * Why this exists as one function
 * -------------------------------
 * `apiService` signals a failed request by throwing an `ApiException` whose
 * `message` is the EMPTY STRING (the shape used in 311 places). Every page that
 * displays an error gates its error block on truthiness — `{error && …}` — and
 * gates its content on the negation — `{!error && …}`. An empty string therefore
 * throws the failure away and renders the CONTENT branch: the empty state.
 *
 * That is not a cosmetic slip. It has produced, repeatedly:
 *   - "No hay mapeos de atributos configurados" + "Crear el primer mapeo" while
 *     dozens existed — an invitation to create a duplicate (#953)
 *   - a "(0 activos)" catalogue with no error while every request was failing
 *   - "Requieren atención: 0" — a clean bill of health — over a failed load
 *
 * Four copies of this ladder had accumulated, each with its own comment
 * explaining the same trap. A rule that lives in four places is a rule that will
 * be fixed in three of them; this is the one implementation.
 *
 * The ladder, in order:
 *   1. the error's own message, when it says something
 *   2. the error CODE (`NETWORK_ERROR`, `ADMIN_ASSETS_FETCH_FAILED`) — not pretty,
 *      but identifiable, and vastly better than "unknown reason"
 *   3. the caller's fallback, so the sentence can be localized per surface
 *
 * Returns `null` only for a non-Error value with no fallback given — the one case
 * where there is genuinely nothing to say.
 */
export function extractDisplayableErrorMessage(error: unknown, fallback?: string): string | null {
  const raw = error instanceof Error ? error.message : ''
  const code = error instanceof ApiException ? error.code : undefined

  // `||` is the point, and `??` would be wrong: the value being rejected here is
  // the EMPTY STRING, which `??` passes through untouched. That pass-through IS
  // the bug this function exists to prevent.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const message = raw.trim() || code || fallback

  return message !== undefined && message !== '' ? message : null
}
